import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Card, CardHeader, Input, Select } from '@/components/common';
import type { MemberStructure } from '@/hooks/useInsuranceTypeSchema';
import {
  emptyMember,
  MEMBER_GENDERS,
  MEMBER_RELATIONS,
  type MemberRow,
} from '@/pages/policies/healthStructure';

type Props = {
  structure: MemberStructure;
  members: MemberRow[];
  onChange: (rows: MemberRow[]) => void;
  /** Field-keyed errors (`members`, `members[2].dob`) from validation or the server. */
  errors?: Record<string, string>;
  /** False while an untouched legacy policy's placeholder rows are shown (nothing is mandatory yet). */
  fieldsRequired?: boolean;
};

const TITLES: Record<MemberStructure['mode'], { title: string; subtitle: string }> = {
  individual: { title: 'Insured Person', subtitle: 'This product covers exactly one person.' },
  floater: {
    title: 'Insured Members',
    subtitle: 'Family floater: one Self plus the family members sharing the sum insured.',
  },
  group: {
    title: 'Insured Members',
    subtitle: 'Optional for group policies. Large rosters can be tracked by lives covered instead.',
  },
};

/**
 * The repeating insured-members block of the health Add/Edit Policy form (#332).
 * Everything about its shape comes from `structure`, which the server resolves
 * from the product / group, so this component never looks at a group code.
 */
export function InsuredMembersBlock({ structure, members, onChange, errors = {}, fieldsRequired = true }: Props) {
  const { mode } = structure;
  const fixedSingle = mode === 'individual';
  const atMax = structure.max != null && members.length >= structure.max;
  const overMax = structure.max != null && members.length > structure.max;
  const { title, subtitle } = TITLES[mode];

  const update = (uid: string, patch: Partial<MemberRow>) =>
    onChange(members.map((m) => (m.uid === uid ? { ...m, ...patch } : m)));
  const remove = (uid: string) => onChange(members.filter((m) => m.uid !== uid));
  const add = () => onChange([...members, emptyMember()]);

  return (
    <div data-testid="insured-members-block">
    <Card>
      <CardHeader title={title} subtitle={subtitle} />

      {overMax && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          This product covers {structure.max === 1 ? 'one person' : `at most ${structure.max} people`}, but{' '}
          {members.length} are listed. Remove the extra rows before saving; nothing has been deleted.
        </p>
      )}
      {errors.members && (
        <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-400">
          {errors.members}
        </p>
      )}

      <div className="space-y-6">
        {members.map((m, i) => {
          const err = (k: string) => errors[`members[${i}].${k}`];
          return (
            <div
              key={m.uid}
              data-testid="member-row"
              className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
            >
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {fixedSingle ? 'Insured person' : `Member ${i + 1}`}
                </p>
                {(!fixedSingle || members.length > 1) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => remove(m.uid)}
                    leftIcon={<TrashIcon className="h-4 w-4" />}
                    aria-label={`Remove member ${i + 1}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Input
                  label="Member name"
                  name={`member-${m.uid}-name`}
                  required={fieldsRequired}
                  maxLength={255}
                  value={m.name}
                  error={err('name')}
                  onChange={(e) => update(m.uid, { name: e.target.value })}
                />
                <Input
                  label="Member date of birth"
                  name={`member-${m.uid}-dob`}
                  type="date"
                  required={fieldsRequired}
                  value={m.dob}
                  error={err('dob')}
                  onChange={(e) => update(m.uid, { dob: e.target.value })}
                />
                <Select
                  label="Member gender"
                  name={`member-${m.uid}-gender`}
                  required={fieldsRequired}
                  options={MEMBER_GENDERS}
                  placeholder="Select gender"
                  value={m.gender}
                  error={err('gender')}
                  onChange={(e) => update(m.uid, { gender: e.target.value })}
                />
                {fixedSingle ? null : (
                  <Select
                    label="Member relation"
                    name={`member-${m.uid}-relation`}
                    required={structure.relation_required && fieldsRequired}
                    options={MEMBER_RELATIONS}
                    placeholder="Select relation"
                    value={m.relation}
                    error={err('relation')}
                    onChange={(e) => update(m.uid, { relation: e.target.value })}
                  />
                )}
                <Input
                  label="Pre-existing conditions"
                  name={`member-${m.uid}-ped`}
                  maxLength={500}
                  placeholder="e.g. Diabetes, Asthma"
                  helperText="Optional, comma separated"
                  value={m.ped}
                  onChange={(e) => update(m.uid, { ped: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Height (cm)"
                    name={`member-${m.uid}-height`}
                    inputMode="numeric"
                    value={m.height_cm}
                    onChange={(e) => update(m.uid, { height_cm: e.target.value.replace(/\D/g, '') })}
                  />
                  <Input
                    label="Weight (kg)"
                    name={`member-${m.uid}-weight`}
                    inputMode="numeric"
                    value={m.weight_kg}
                    onChange={(e) => update(m.uid, { weight_kg: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!fixedSingle && (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={add}
            disabled={atMax}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Add member
          </Button>
          {atMax && (
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Maximum of {structure.max} members reached.
            </span>
          )}
        </div>
      )}
    </Card>
    </div>
  );
}

export default InsuredMembersBlock;
